import { isToday, isTomorrow } from './Comparison';
import { IsoDate, isIsoDate, assertIsoDate, IsoDateFormat } from './IsoDate';
import IsoTimestamp, { assertIsoTimestamp } from './IsoTimestamp';
import getTZAbbr from './getTZAbbr';
import { getLocalTimezone } from './getLocalTimezone';
import {
  format,
  formatDuration as dateFnsFormatDuration,
  isSameDay as dateFnsIsSameDay,
  intervalToDuration,
} from 'date-fns';
import { getDateFnsLocale } from './getDateFnsLocale';
import { format as formatTZ, toZonedTime } from 'date-fns-tz';
import { formatDateTime, formatDateRangeIntl, toIntlDate, DATE_FNS_PATTERN_TO_FORMAT_NAME } from '@modernloop/i18n';
import type { DateTimeFormatName } from '@modernloop/i18n';

/** Translate a date-fns display pattern to the locale-aware catalog and render it.
 *  Always pass the ORIGINAL Iso value (never a toZonedTime result). */
const formatPattern = (value: IsoTimestamp | IsoDate, pattern: string, timezone?: string): string => {
  const name = DATE_FNS_PATTERN_TO_FORMAT_NAME[pattern];
  if (name) {
    return formatDateTime(value, name, { timeZone: timezone });
  }
  // Uncatalogued pattern: preserve legacy (non-localized) date-fns behavior so existing
  // callers don't break. New display patterns are surfaced by the lint rule + tests, not a throw.
  if (isIsoDate(value)) {
    // Date-only: format the calendar date directly with NO timezone. toIntlDate gives a
    // local-noon Date; passing a timeZone here (date-fns-tz) can shift the rendered day at
    // far offsets (noon + up to +14h crosses midnight). Plain date-fns format preserves the day.
    return format(toIntlDate(value), pattern);
  }
  const tz = timezone ?? getLocalTimezone();
  return formatTZ(toZonedTime(value, tz), pattern, { timeZone: tz });
};

const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 1440;

export const getCurrentDateIso = (): IsoTimestamp => assertIsoTimestamp(new Date().toISOString(), 'development');

/**
 * Formats a date using the specified timezone
 * @param date - Date to format
 * @param formatString - https://date-fns.org/v2.24.0/docs/format
 * @param tz - the timezone
 */
export const formatToTimeZone = (date: IsoTimestamp, formatString: string, tz: string): string => {
  const name = DATE_FNS_PATTERN_TO_FORMAT_NAME[formatString];
  if (name) {
    return formatDateTime(date, name, { timeZone: tz });
  }
  // Machine/uncatalogued format: keep raw, do not localize.
  const zonedDate = toZonedTime(date, tz);
  return formatTZ(zonedDate, formatString, { timeZone: tz });
};

/**
 * Formats a date interval in a string format.
 * @param startAt - start of the interval
 * @param endAt - end of the interval
 * @returns string representing the duration of the interval in the format "DD d HH h MM m"
 */
export const getDurationStringOfInterval = (startAt: IsoTimestamp, endAt: IsoTimestamp) => {
  const { days, hours, minutes } = intervalToDuration({ start: new Date(startAt), end: new Date(endAt) });

  return `${days ? `${days}d ` : ''}${hours ? `${hours}h ` : ''}${minutes ? `${minutes}m` : ''}`;
};

/**
 * Formats a date time range in a string format.
 * By default, it formats the date range in the format "MMM d, h:mm aaa - h:mm aaa".
 * If the start and end are on the same day, it formats the date range in the format "MMM d, h:mm aaa - h:mm aaa".
 * If the start and end are on different days, it formats the date range in the format "MMM d, h:mm aaa - MMM d, h:mm aaa".
 */
export const formatDateTimeRange = ({
  start,
  end,
  timezone,
  startDateFormat = 'MMM d, h:mm aaa',
  endDateFormat = startDateFormat,
  showTzAbbr = false,
}: {
  start: IsoTimestamp;
  end: IsoTimestamp;
  timezone: string;
  startDateFormat?: string;
  endDateFormat?: string;
  showTzAbbr?: boolean;
}) => {
  const sameDay = dateFnsIsSameDay(toZonedTime(start, timezone), toZonedTime(end, timezone));

  const startStr = formatPattern(start, startDateFormat, timezone);
  const endStr = sameDay ? formatPattern(end, 'h:mm a', timezone) : formatPattern(end, endDateFormat, timezone);
  let result = `${startStr} - ${endStr}`;
  if (showTzAbbr) {
    result += ' ' + getTZAbbr(timezone);
  }
  return result;
};

export const formatDateRange = (
  start: IsoTimestamp,
  end: IsoTimestamp,
  timezone: string,
  startDateFormat = 'EEEE, MMMM d, yyyy',
  endDateFormat = startDateFormat
): string => {
  const sameDay = dateFnsIsSameDay(toZonedTime(start, timezone), toZonedTime(end, timezone));

  if (sameDay) {
    return formatPattern(start, startDateFormat, timezone);
  }
  return `${formatPattern(start, startDateFormat, timezone)} - ${formatPattern(end, endDateFormat, timezone)}`;
};

export const formatDate = (date: IsoTimestamp | IsoDate, timezone: string, dateFormat: string): string => {
  return formatPattern(date, dateFormat, timezone);
};

export const formatDay = (dayDate: IsoTimestamp, timezone: string, dayFormat: string): string => {
  let result = formatPattern(dayDate, dayFormat, timezone);
  if (isToday(dayDate)) {
    result = `Today ` + result;
  } else if (isTomorrow(dayDate)) {
    result = `Tomorrow ` + result;
  }
  return result;
};

export const formatTimeRange = (
  start: IsoTimestamp,
  end: IsoTimestamp,
  timezone: string,
  showTzAbbr = false
): string => {
  let result = `${formatPattern(start, 'h:mm a', timezone)} - ${formatPattern(end, 'h:mm a', timezone)}`;
  if (showTzAbbr) {
    result += ' ' + getTZAbbr(timezone);
  }
  return result;
};

export const formatDurationFromMinutes = (durationInMinutes: number, options?: { abbreviate?: boolean }) => {
  const { abbreviate = false } = options || {};
  const duration = dateFnsFormatDuration(
    {
      minutes: durationInMinutes % MINUTES_IN_HOUR,
      hours: Math.floor((durationInMinutes % MINUTES_IN_DAY) / MINUTES_IN_HOUR),
      days: Math.floor(durationInMinutes / MINUTES_IN_DAY),
    },
    { locale: getDateFnsLocale() }
  );

  if (abbreviate) {
    // NOTE: abbreviation is English-only; for non-English locales the localized words are shown unabbreviated.
    return duration.replaceAll('hour', 'hr').replaceAll('minute', 'min');
  }
  return duration;
};

/**
 * If start & end are same date show start date only
 * If Same month but different date Dec 22 – 25, 2023
 * If Different year Dec 29, 2023 – Jan 1, 2024
 */
export const formatDayOffDateRange = (startAtDate: IsoDate, endAtDate: IsoDate): string => {
  if (!startAtDate || !endAtDate) {
    return '';
  }
  // Intl.formatRange collapses shared month/year automatically, covering the
  // same-day / same-month / same-year / cross-year cases described above.
  return formatDateRangeIntl(startAtDate, endAtDate, 'MonthDayYear');
};

/** Format a JS Date as a locale-aware LOCAL calendar date (date-only, no timezone). */
export const formatLocalizedCalendarDate = (date: Date, name: DateTimeFormatName): string =>
  formatDateTime(assertIsoDate(format(date, IsoDateFormat)), name);
